import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Microscope, Anchor, Scale, Mail } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import { STITCH_IMAGES } from '../../constants/stitchAssets';
import { subscribeInsightsSearchQuery } from '../../utils/insightsSearchBus';

const articles = [
  {
    to: '/insights/methodology',
    title: 'How we build the Global Trade Index (GTI)',
    description:
      'Data sources, event taxonomy, scoring logic, refresh cadence, and known limitations—written for analysts who need to interpret the dashboard responsibly.',
    icon: Microscope,
    tag: 'methodology',
  },
  {
    to: '/insights/hormuz-trade-shocks',
    title: 'From Hormuz disruption to prices and freight: a supply-chain lens',
    description:
      'An evergreen explainer on why a narrow chokepoint matters for crude flows, refining margins, insurance, and routing—and how to read risk without sensationalism.',
    icon: Anchor,
    tag: 'chokepoints',
  },
  {
    to: '/insights/tariffs-vs-legislation',
    title: 'Tariffs vs. legislation on TradeRadar: what each module shows',
    description:
      'How to use the tariffs view versus the legislation tracker, avoid mixing correlation with causation, and cross-check AI-extracted fields against primary texts.',
    icon: Scale,
    tag: 'tariffs',
  },
];

const FILTERS = [
  { id: 'all', label: 'All briefs' },
  { id: 'methodology', label: 'Methodology' },
  { id: 'chokepoints', label: 'Chokepoints' },
  { id: 'tariffs', label: 'Tariffs & law' },
];

const InsightsIndex = () => {
  const title = 'Trade Research & Insights | TradeRadar';
  const description =
    'Methodology and long-form guides on global trade risk, the Global Trade Index, chokepoints, tariffs, and legislation—original analysis to complement the data dashboards.';

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');

  useEffect(() => {
    return subscribeInsightsSearchQuery(setSearch);
  }, []);

  const filteredArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      const tagOk = filter === 'all' || a.tag === filter;
      if (!tagOk) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.tag.includes(q)
      );
    });
  }, [filter, search]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    const body = subscribeEmail.trim()
      ? `Please add this email to insights updates: ${subscribeEmail}`
      : 'Please add me to insights updates.';
    window.location.href = `mailto:contact@traderadarglobal.com?subject=${encodeURIComponent(
      'TradeRadar insights updates'
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <article className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <SEOHead
        title={title}
        description={description}
        path="/insights"
        keywords="global trade methodology, GTI explained, trade risk research, Hormuz trade analysis"
      />

      <header className="mb-8">
        <div className="mb-2 flex items-center gap-2 text-primary-600 dark:text-primary">
          <BookOpen className="h-6 w-6" />
          <span className="label-intel !text-primary-600 dark:!text-primary">Insights & intelligence hub</span>
        </div>
        <h1 className="mb-3 font-headline text-3xl font-bold text-slate-900 dark:text-on-surface md:text-4xl">
          Insights & <span className="text-primary-600 dark:text-primary">Intelligence</span> Hub
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-on-surface-variant">
          First-party research and methodology so you can interpret tariffs, GTI scores, and chokepoint risk
          alongside primary sources—not as a substitute for legal or investment advice.
        </p>
        <p className="mt-3 text-xs text-slate-500 dark:text-outline">
          Byline: TradeRadar Editorial · Last hub review: March 2026
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide transition ${
                filter === f.id
                  ? 'bg-primary text-on-primary shadow-sm dark:bg-primary dark:text-on-primary-container'
                  : 'border border-outline-variant/25 bg-surface-container text-slate-600 hover:border-primary/40 dark:text-on-surface-variant'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="relative min-h-[280px] overflow-hidden rounded-3xl border border-outline-variant/15 lg:col-span-8">
          <img
            src={STITCH_IMAGES.insightsFeatured.src}
            alt={STITCH_IMAGES.insightsFeatured.alt}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/20" />
          <div className="relative z-10 flex h-full flex-col justify-end p-6 md:p-8">
            <span className="mb-2 inline-flex w-fit rounded-full bg-error/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              Featured brief
            </span>
            <h2 className="max-w-xl font-headline text-2xl font-bold text-slate-900 dark:text-on-surface md:text-3xl">
              Hormuz shocks, freight, and prices
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-600 dark:text-on-surface-variant">
              How a narrow chokepoint propagates through energy markets and trade routes—written for analysts.
            </p>
            <Link
              to="/insights/hormuz-trade-shocks"
              className="btn-intel-primary mt-5 inline-flex w-fit items-center px-5 py-2.5 text-sm font-semibold"
            >
              Read full brief
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to="/middle-east-impact-dashboard"
              className="mt-2 inline-flex text-xs font-medium text-primary-600 underline dark:text-primary"
            >
              Open Middle East impact dashboard
            </Link>
          </div>
        </div>

        <div className="rounded-3xl border border-outline-variant/15 bg-surface-container p-6 dark:bg-surface-container-low lg:col-span-4">
          <div className="mb-3 flex items-center gap-2 text-primary-600 dark:text-primary">
            <Mail className="h-5 w-5" />
            <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Subscribe</h2>
          </div>
          <p className="mb-4 text-sm text-slate-600 dark:text-on-surface-variant">
            Request occasional methodology and hub updates via email (opens your mail client—no account stored on-site).
          </p>
          <form onSubmit={handleSubscribe} className="space-y-3">
            <label className="block text-xs font-medium text-slate-600 dark:text-on-surface-variant" htmlFor="insights-email">
              Email (optional)
            </label>
            <input
              id="insights-email"
              type="email"
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full rounded-stitch-lg border border-outline-variant/25 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:bg-surface-container-high dark:text-on-surface"
            />
            <button type="submit" className="w-full rounded-stitch-lg bg-primary py-2.5 text-sm font-semibold text-on-primary">
              Send request
            </button>
          </form>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="mb-4 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Latest briefs</h2>
        {filteredArticles.length === 0 ? (
          <p className="rounded-2xl border border-outline-variant/15 bg-surface-container p-6 text-sm text-slate-600 dark:text-on-surface-variant">
            No articles match your filters or search. Clear the navbar search or choose &quot;All briefs&quot;.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredArticles.map(({ to, title: t, description: d, icon: Icon, tag }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="group flex h-full flex-col rounded-2xl border border-outline-variant/15 bg-surface-container p-5 transition hover:border-primary/35 dark:bg-surface-container-low dark:hover:border-primary/40"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-700 dark:text-primary">
                      {tag.replace('-', ' ')}
                    </span>
                    <Icon className="h-5 w-5 text-primary-600 opacity-80 dark:text-primary" aria-hidden />
                  </div>
                  <h3 className="font-headline text-base font-semibold text-slate-900 group-hover:text-primary-600 dark:text-on-surface dark:group-hover:text-primary">
                    {t}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-on-surface-variant">{d}</p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary">
                    Read
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-3xl border border-outline-variant/15 bg-gradient-to-br from-slate-50 to-white p-6 dark:from-surface-container-low dark:to-surface-container md:p-8">
        <h2 className="mb-6 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Methodology & standards</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-on-surface">GTI methodology</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              Scoring inputs, refresh cadence, and how to read country tiles without over-interpreting AI extracts.
            </p>
            <Link to="/insights/methodology" className="mt-2 inline-flex text-sm font-medium text-primary-600 dark:text-primary">
              Open guide <ArrowRight className="ml-1 inline h-4 w-4" />
            </Link>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-on-surface">Editorial standards</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              Corrections policy, sourcing expectations, and how we label uncertainty in fast-moving trade news.
            </p>
            <Link to="/editorial-standards" className="mt-2 inline-flex text-sm font-medium text-primary-600 dark:text-primary">
              Read standards <ArrowRight className="ml-1 inline h-4 w-4" />
            </Link>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-on-surface">Hormuz data hub</h3>
            <p className="text-sm text-slate-600 dark:text-on-surface-variant">
              Scenario tools, dependency maps, and risk dashboards that pair with the Hormuz research note.
            </p>
            <Link to="/strait-of-hormuz" className="mt-2 inline-flex text-sm font-medium text-primary-600 dark:text-primary">
              Explore hub <ArrowRight className="ml-1 inline h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
};

export default InsightsIndex;
